<template>
  <Teleport to="body">
    <Transition name="progress-tracker-overlay">
      <div
        v-if="show"
        class="progress-tracker-overlay progress-tracker-overlay--glass"
        role="dialog"
        aria-modal="true"
        aria-labelledby="progress-tracker-title"
      >
        <div class="progress-tracker-modal progress-tracker-modal--glass" @click.stop>
          <div class="progress-tracker-header progress-tracker-header--glass">
            <h5 id="progress-tracker-title" class="progress-tracker-title">
              <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
              {{ title }}
            </h5>
          </div>

          <div class="progress-tracker-body progress-tracker-body--glass">
            <div class="progress-tracker-icon">
              <i v-if="!isComplete" class="bi bi-hourglass-split" aria-hidden="true"></i>
              <i v-else class="bi bi-check-circle-fill" aria-hidden="true"></i>
            </div>

            <div class="progress-tracker-summary">
              <h6 class="progress-tracker-main-message">{{ mainMessage }}</h6>
              <p class="progress-tracker-sub-message">{{ subMessage }}</p>
            </div>

            <div class="progress-tracker-bar-container">
              <div class="progress-tracker-bar">
                <div
                  class="progress-tracker-fill"
                  :style="{ width: progressPercentage + '%' }"
                ></div>
              </div>
              <div class="progress-tracker-text">
                {{ processedCount }} of {{ totalCount }} {{ itemLabel }} processed
                ({{ progressPercentage }}%)
              </div>
            </div>

            <div class="progress-tracker-details">
              <div class="progress-tracker-stat">
                <span class="progress-tracker-stat-label">Total</span>
                <span class="progress-tracker-stat-value">{{ totalCount }}</span>
              </div>
              <div class="progress-tracker-stat">
                <span class="progress-tracker-stat-label">Processing</span>
                <span class="progress-tracker-stat-value">{{ processingCount }}</span>
              </div>
              <div class="progress-tracker-stat">
                <span class="progress-tracker-stat-label">Completed</span>
                <span class="progress-tracker-stat-value progress-tracker-success">
                  {{ completedCount }}
                </span>
              </div>
              <div v-if="skippedCount > 0" class="progress-tracker-stat">
                <span class="progress-tracker-stat-label">Skipped</span>
                <span class="progress-tracker-stat-value progress-tracker-warning">
                  {{ skippedCount }}
                </span>
              </div>
              <div v-if="errorCount > 0" class="progress-tracker-stat">
                <span class="progress-tracker-stat-label">Errors</span>
                <span class="progress-tracker-stat-value progress-tracker-error">
                  {{ errorCount }}
                </span>
              </div>
            </div>

            <div v-if="errorCount > 0" class="progress-tracker-errors">
              <div class="progress-tracker-error-header">
                <i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
                <span>Errors encountered</span>
              </div>
              <div class="progress-tracker-error-list">
                <div
                  v-for="(error, index) in errors"
                  :key="index"
                  class="progress-tracker-error-item"
                >
                  <span class="progress-tracker-error-item-name">{{ error.itemName }}:</span>
                  <span class="progress-tracker-error-message">{{ error.message }}</span>
                </div>
              </div>
            </div>

            <div class="progress-tracker-actions progress-tracker-actions--glass">
              <SecondaryButton
                v-if="!isComplete"
                label="Close"
                icon="bi bi-x-lg"
                :disabled="isProcessing"
                @click="handleClose"
              />
              <PrimaryButton
                v-if="isComplete"
                :label="doneButtonLabel"
                icon="bi bi-check-lg"
                @click="handleClose"
              />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'ProgressTracker',
  components: {
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Processing Request',
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    processedCount: {
      type: Number,
      default: 0,
    },
    completedCount: {
      type: Number,
      default: 0,
    },
    skippedCount: {
      type: Number,
      default: 0,
    },
    processingCount: {
      type: Number,
      default: 0,
    },
    errorCount: {
      type: Number,
      default: 0,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
    itemLabel: {
      type: String,
      default: 'items',
    },
  },
  emits: ['close'],
  computed: {
    progressPercentage() {
      if (this.totalCount === 0) return 0;
      return Math.round((this.processedCount / this.totalCount) * 100);
    },
    isComplete() {
      return this.processedCount >= this.totalCount && this.totalCount > 0;
    },
    hasErrors() {
      return this.errorCount > 0;
    },
    doneButtonLabel() {
      return this.hasErrors ? 'Continue' : 'Done';
    },
    mainMessage() {
      if (this.hasErrors && this.isComplete) {
        return `Completed with ${this.errorCount} error(s)`;
      }
      if (this.isComplete) {
        return `Successfully processed ${this.completedCount} ${this.itemLabel}`;
      }
      if (this.isProcessing) {
        return `Processing ${this.itemLabel}...`;
      }
      return 'Preparing to process...';
    },
    subMessage() {
      if (this.hasErrors && this.isComplete) {
        return `${this.completedCount} ${this.itemLabel} processed successfully, ${this.errorCount} failed`;
      }
      if (this.isComplete) {
        const skippedText =
          this.skippedCount > 0 ? ` ${this.skippedCount} duplicates skipped.` : '';
        return `All ${this.itemLabel} have been processed${skippedText}`;
      }
      if (this.isProcessing) {
        return 'Please wait while we process your request...';
      }
      return `Getting ready to process ${this.totalCount} ${this.itemLabel}`;
    },
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
@import './ProgressTracker.css';
</style>
